import { useState } from 'react';
import { FileText, Download, Palette, Check, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';

// Paletas de colores predefinidas
const PALETAS = {
  corporativa: {
    nombre: 'Corporativa Despacho',
    primario: '#31313D',      // Gunmetal PF Advisors
    secundario: '#B27707',    // Amarillo de marca oscurecido (legible sobre blanco)
    acento: '#F6AA19',        // Bright Yellow PF Advisors
    texto: '#31313D',
    textoClaro: '#757070',
    fondo: '#FFF0D9',         // Papaya Whip
    tabla: {
      header: '#31313D',
      headerText: '#ffffff',
      row1: '#ffffff',
      row2: '#FFF0D9'
    }
  },
  aplicacion: {
    nombre: 'Pool Bancario App',
    primario: '#31313D',      // Gunmetal (sidebar)
    secundario: '#F6AA19',    // Amarillo corporativo PF Advisors
    acento: '#FCCD81',        // Topaz
    texto: '#31313D',
    textoClaro: '#757070',
    fondo: '#FAF8F5',
    tabla: {
      header: '#31313D',
      headerText: '#ffffff',
      row1: '#ffffff',
      row2: '#F3F0EB'
    }
  },
  moderna: {
    nombre: 'Moderna Minimalista',
    primario: '#0f172a',      // Slate 900
    secundario: '#6366f1',    // Indigo
    acento: '#10b981',        // Emerald
    texto: '#0f172a',
    textoClaro: '#64748b',
    fondo: '#ffffff',
    tabla: {
      header: '#0f172a',
      headerText: '#ffffff',
      row1: '#ffffff',
      row2: '#f8fafc'
    }
  },
  clasica: {
    nombre: 'Clásica Formal',
    primario: '#1f2937',      // Gray 800
    secundario: '#374151',    // Gray 700
    acento: '#991b1b',        // Red oscuro
    texto: '#111827',
    textoClaro: '#6b7280',
    fondo: '#fafafa',
    tabla: {
      header: '#1f2937',
      headerText: '#ffffff',
      row1: '#ffffff',
      row2: '#f3f4f6'
    }
  }
};

// Secciones disponibles para el informe
const SECCIONES = [
  { id: 'resumen', nombre: 'Resumen Ejecutivo', descripcion: 'Métricas principales y visión general' },
  { id: 'graficas', nombre: 'Gráficas', descripcion: 'Gráficos de distribución y evolución de la deuda' },
  { id: 'largoplazo', nombre: 'Operaciones a Largo Plazo', descripcion: 'Tabla detallada de préstamos' },
  { id: 'cortoplazo', nombre: 'Operaciones a Corto Plazo', descripcion: 'Tabla de líneas de crédito' },
  { id: 'distribucion', nombre: 'Distribución de Deuda', descripcion: 'Análisis por producto y entidad' },
  { id: 'evolucion', nombre: 'Evolución Anual', descripcion: 'Proyección de pagos y capital pendiente' },
  { id: 'proyecciones', nombre: 'Cuadro de Amortización', descripcion: 'Detalle mensual de cada préstamo' },
];

const formatCurrency = (value) => {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      return `${match[3]}/${match[2]}/${match[1]}`;
    }
    return dateStr;
  } catch {
    return dateStr;
  }
};

export default function PDFReportGenerator({ 
  empresa, 
  stats, 
  operacionesLP, 
  operacionesCP, 
  proyeccionesAnuales,
  isOpen,
  onClose 
}) {
  const [generando, setGenerando] = useState(false);
  const [paletaSeleccionada, setPaletaSeleccionada] = useState('aplicacion');
  const [seccionesActivas, setSeccionesActivas] = useState({
    resumen: true,
    graficas: true,
    largoplazo: true,
    cortoplazo: true,
    distribucion: true,
    evolucion: true,
    proyecciones: false // Por defecto desactivado porque puede ser muy largo
  });

  const toggleSeccion = (id) => {
    setSeccionesActivas(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const paleta = PALETAS[paletaSeleccionada];

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? [
      parseInt(result[1], 16),
      parseInt(result[2], 16),
      parseInt(result[3], 16)
    ] : [0, 0, 0];
  };

  const generarPDF = async () => {
    if (!empresa) {
      toast.error('No hay empresa seleccionada');
      return;
    }

    setGenerando(true);

    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      let yPos = margin;

      // Función helper para añadir nueva página si es necesario
      const checkNewPage = (neededSpace = 30) => {
        if (yPos + neededSpace > pageHeight - margin) {
          doc.addPage();
          yPos = margin;
          return true;
        }
        return false;
      };

      // Función para añadir encabezado de sección
      const addSectionHeader = (titulo) => {
        checkNewPage(20);
        doc.setFillColor(...hexToRgb(paleta.primario));
        doc.rect(margin, yPos, pageWidth - margin * 2, 8, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(255, 255, 255);
        doc.text(titulo.toUpperCase(), margin + 3, yPos + 5.5);
        yPos += 12;
        doc.setTextColor(...hexToRgb(paleta.texto));
      };

      // ========== PORTADA ==========
      // Fondo de cabecera
      doc.setFillColor(...hexToRgb(paleta.primario));
      doc.rect(0, 0, pageWidth, 60, 'F');

      // Título
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(24);
      doc.setTextColor(255, 255, 255);
      doc.text('INFORME POOL BANCARIO', pageWidth / 2, 25, { align: 'center' });

      // Subtítulo
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(14);
      doc.text(empresa.nombre, pageWidth / 2, 38, { align: 'center' });

      // Fecha
      doc.setFontSize(10);
      const fechaHoy = new Date().toLocaleDateString('es-ES', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
      doc.text(`Fecha de generación: ${fechaHoy}`, pageWidth / 2, 50, { align: 'center' });

      yPos = 75;

      // ========== RESUMEN EJECUTIVO ==========
      if (seccionesActivas.resumen && stats) {
        addSectionHeader('Resumen Ejecutivo');

        // Métricas en 2x2 grid
        const metricWidth = (pageWidth - margin * 2 - 10) / 2;
        const metricas = [
          { label: 'Capital Pendiente L/P', value: formatCurrency(stats.capital_pendiente_lp) },
          { label: 'Dispuesto C/P', value: formatCurrency(stats.dispuesto_cp) },
          { label: `Cuota Anual Total ${proyeccionesAnuales && proyeccionesAnuales.length > 0 ? proyeccionesAnuales[0].año : new Date().getFullYear()}`, value: formatCurrency(
            proyeccionesAnuales && proyeccionesAnuales.length > 0 
              ? proyeccionesAnuales[0].totalAmortizacion + proyeccionesAnuales[0].totalInteres 
              : 0
          ) },
          { label: 'Operaciones Activas', value: stats.operaciones_activas?.toString() || '0' }
        ];

        doc.setFontSize(9);
        metricas.forEach((m, i) => {
          const x = margin + (i % 2) * (metricWidth + 10);
          const y = yPos + Math.floor(i / 2) * 18;

          doc.setFillColor(...hexToRgb(paleta.fondo));
          doc.roundedRect(x, y, metricWidth, 15, 2, 2, 'F');
          doc.setDrawColor(...hexToRgb(paleta.secundario));
          doc.roundedRect(x, y, metricWidth, 15, 2, 2, 'S');

          doc.setFont('helvetica', 'normal');
          doc.setTextColor(...hexToRgb(paleta.textoClaro));
          doc.text(m.label, x + 3, y + 5);

          doc.setFont('helvetica', 'bold');
          doc.setTextColor(...hexToRgb(paleta.primario));
          doc.setFontSize(12);
          doc.text(m.value, x + 3, y + 12);
          doc.setFontSize(9);
        });

        yPos += 45;
      }

      // ========== GRÁFICAS ==========
      if (seccionesActivas.graficas) {
        // Capturar gráficos de donut - uno debajo de otro en tamaño normal
        const donutCharts = document.querySelectorAll('[data-testid^="chart-"]');
        if (donutCharts.length > 0) {
          checkNewPage(80);
          addSectionHeader('Gráficas de Distribución');

          // Capturar cada gráfico de donut por separado, uno debajo de otro
          for (let i = 0; i < Math.min(donutCharts.length, 3); i++) {
            const chart = donutCharts[i];
            try {
              const canvas = await html2canvas(chart, {
                scale: 3,
                backgroundColor: '#ffffff',
                logging: false,
                useCORS: true
              });
              
              const imgData = canvas.toDataURL('image/png');
              
              // Tamaño normal centrado - mantener proporciones originales
              const maxWidth = 120; // Ancho máximo en mm
              const aspectRatio = canvas.height / canvas.width;
              const imgWidth = Math.min(maxWidth, pageWidth - margin * 2);
              const imgHeight = imgWidth * aspectRatio;
              
              // Verificar si necesitamos nueva página
              checkNewPage(imgHeight + 15);
              
              // Centrar horizontalmente
              const xPos = (pageWidth - imgWidth) / 2;
              
              doc.addImage(imgData, 'PNG', xPos, yPos, imgWidth, imgHeight);
              yPos += imgHeight + 10;
              
            } catch (e) {
              console.error('Error capturing donut chart:', e);
            }
          }
          
          yPos += 5;
        }

        // Capturar gráficos de evolución (barras y línea) - uno por uno para mejor calidad
        const barChart = document.querySelector('[data-testid="bar-chart-container"]');
        const lineChart = document.querySelector('[data-testid="line-chart-container"]');
        
        if (barChart || lineChart) {
          checkNewPage(90);
          addSectionHeader('Evolución de la Deuda a Largo Plazo');

          // Capturar gráfico de barras
          if (barChart) {
            try {
              const canvas = await html2canvas(barChart, {
                scale: 3,
                backgroundColor: '#ffffff',
                logging: false
              });
              const imgData = canvas.toDataURL('image/png');
              
              const imgWidth = (pageWidth - margin * 2 - 10) / 2;
              const aspectRatio = canvas.height / canvas.width;
              const imgHeight = Math.min(70, imgWidth * aspectRatio);
              
              doc.addImage(imgData, 'PNG', margin, yPos, imgWidth, imgHeight);
              
              // Capturar gráfico de línea al lado
              if (lineChart) {
                const canvas2 = await html2canvas(lineChart, {
                  scale: 3,
                  backgroundColor: '#ffffff',
                  logging: false
                });
                const imgData2 = canvas2.toDataURL('image/png');
                doc.addImage(imgData2, 'PNG', margin + imgWidth + 10, yPos, imgWidth, imgHeight);
              }
              
              yPos += imgHeight + 10;
            } catch (e) {
              console.error('Error capturing evolution charts:', e);
            }
          }
        }
      }

      // ========== OPERACIONES A LARGO PLAZO ==========
      if (seccionesActivas.largoplazo && operacionesLP && operacionesLP.length > 0) {
        checkNewPage(50);
        addSectionHeader('Operaciones a Largo Plazo');

        const lpData = operacionesLP.map(op => [
          op.entidad_financiera || '-',
          op.tipologia_producto || '-',
          formatCurrency(op.importe_solicitado),
          formatCurrency(op.capital_pendiente),
          formatCurrency(op.cuota_mensual),
          formatDate(op.fecha_fin)
        ]);

        autoTable(doc, {
          startY: yPos,
          head: [['Entidad', 'Tipo', 'Importe', 'Capital Pte.', 'Cuota', 'Vencimiento']],
          body: lpData,
          theme: 'grid',
          headStyles: {
            fillColor: hexToRgb(paleta.tabla.header),
            textColor: hexToRgb(paleta.tabla.headerText),
            fontStyle: 'bold',
            fontSize: 8
          },
          bodyStyles: {
            fontSize: 7,
            textColor: hexToRgb(paleta.texto)
          },
          alternateRowStyles: {
            fillColor: hexToRgb(paleta.tabla.row2)
          },
          columnStyles: {
            0: { cellWidth: 30 },
            1: { cellWidth: 25 },
            2: { cellWidth: 25, halign: 'right' },
            3: { cellWidth: 25, halign: 'right' },
            4: { cellWidth: 22, halign: 'right' },
            5: { cellWidth: 22, halign: 'center' }
          },
          margin: { left: margin, right: margin },
          didDrawPage: function(data) {
            yPos = data.cursor.y + 10;
          }
        });

        yPos = doc.lastAutoTable?.finalY + 10 || yPos;

        // Total
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(...hexToRgb(paleta.primario));
        const totalLP = operacionesLP.reduce((sum, op) => sum + (op.capital_pendiente || 0), 0);
        doc.text(`Total Capital Pendiente L/P: ${formatCurrency(totalLP)}`, pageWidth - margin, yPos, { align: 'right' });
        yPos += 10;
      }

      // ========== OPERACIONES A CORTO PLAZO ==========
      if (seccionesActivas.cortoplazo && operacionesCP && operacionesCP.length > 0) {
        checkNewPage(50);
        addSectionHeader('Operaciones a Corto Plazo');

        const cpData = operacionesCP.map(op => [
          op.entidad_financiera || '-',
          op.tipologia_producto || '-',
          formatCurrency(op.limite),
          formatCurrency(op.dispuesto),
          formatCurrency(op.limite - op.dispuesto),
          formatDate(op.fecha_renovacion)
        ]);

        autoTable(doc, {
          startY: yPos,
          head: [['Entidad', 'Tipo', 'Límite', 'Dispuesto', 'Disponible', 'Renovación']],
          body: cpData,
          theme: 'grid',
          headStyles: {
            fillColor: hexToRgb(paleta.tabla.header),
            textColor: hexToRgb(paleta.tabla.headerText),
            fontStyle: 'bold',
            fontSize: 8
          },
          bodyStyles: {
            fontSize: 7,
            textColor: hexToRgb(paleta.texto)
          },
          alternateRowStyles: {
            fillColor: hexToRgb(paleta.tabla.row2)
          },
          columnStyles: {
            0: { cellWidth: 30 },
            1: { cellWidth: 30 },
            2: { cellWidth: 25, halign: 'right' },
            3: { cellWidth: 25, halign: 'right' },
            4: { cellWidth: 25, halign: 'right' },
            5: { cellWidth: 22, halign: 'center' }
          },
          margin: { left: margin, right: margin }
        });

        yPos = doc.lastAutoTable?.finalY + 10 || yPos + 20;

        // Totales
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        const totalLimite = operacionesCP.reduce((sum, op) => sum + (op.limite || 0), 0);
        const totalDispuesto = operacionesCP.reduce((sum, op) => sum + (op.dispuesto || 0), 0);
        doc.text(`Total Límite: ${formatCurrency(totalLimite)} | Dispuesto: ${formatCurrency(totalDispuesto)}`, pageWidth - margin, yPos, { align: 'right' });
        yPos += 10;
      }

      // ========== DISTRIBUCIÓN DE DEUDA ==========
      if (seccionesActivas.distribucion && stats) {
        checkNewPage(60);
        addSectionHeader('Distribución de la Deuda');

        // Por tipo de producto
        if (stats.composicion_producto && stats.composicion_producto.length > 0) {
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(10);
          doc.setTextColor(...hexToRgb(paleta.secundario));
          doc.text('Por Tipo de Producto:', margin, yPos);
          yPos += 6;

          const prodData = stats.composicion_producto.map(p => [
            p.name,
            formatCurrency(p.value),
            `${((p.value / stats.composicion_producto.reduce((s, x) => s + x.value, 0)) * 100).toFixed(1)}%`
          ]);

          autoTable(doc, {
            startY: yPos,
            head: [['Producto', 'Importe', '%']],
            body: prodData,
            theme: 'grid',
            headStyles: {
              fillColor: hexToRgb(paleta.tabla.header),
              textColor: hexToRgb(paleta.tabla.headerText),
              fontStyle: 'bold',
              fontSize: 8
            },
            bodyStyles: { fontSize: 8 },
            columnStyles: {
              0: { cellWidth: 60 },
              1: { cellWidth: 40, halign: 'right' },
              2: { cellWidth: 25, halign: 'center' }
            },
            margin: { left: margin, right: margin },
            tableWidth: 125
          });

          yPos = doc.lastAutoTable?.finalY + 10 || yPos + 20;
        }

        // Por entidad bancaria
        if (stats.distribucion_entidades && stats.distribucion_entidades.length > 0) {
          checkNewPage(40);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(10);
          doc.setTextColor(...hexToRgb(paleta.secundario));
          doc.text('Por Entidad Bancaria:', margin, yPos);
          yPos += 6;

          const entData = stats.distribucion_entidades.slice(0, 10).map(e => [
            e.name,
            formatCurrency(e.value),
            `${((e.value / stats.distribucion_entidades.reduce((s, x) => s + x.value, 0)) * 100).toFixed(1)}%`
          ]);

          autoTable(doc, {
            startY: yPos,
            head: [['Entidad', 'Importe', '%']],
            body: entData,
            theme: 'grid',
            headStyles: {
              fillColor: hexToRgb(paleta.tabla.header),
              textColor: hexToRgb(paleta.tabla.headerText),
              fontStyle: 'bold',
              fontSize: 8
            },
            bodyStyles: { fontSize: 8 },
            columnStyles: {
              0: { cellWidth: 60 },
              1: { cellWidth: 40, halign: 'right' },
              2: { cellWidth: 25, halign: 'center' }
            },
            margin: { left: margin, right: margin },
            tableWidth: 125
          });

          yPos = doc.lastAutoTable?.finalY + 10 || yPos + 20;
        }
      }

      // ========== EVOLUCIÓN ANUAL ==========
      if (seccionesActivas.evolucion && proyeccionesAnuales && proyeccionesAnuales.length > 0) {
        checkNewPage(50);
        addSectionHeader('Evolución Anual de la Deuda a Largo Plazo');

        const evolData = proyeccionesAnuales.map(p => [
          p.año.toString(),
          formatCurrency(p.totalAmortizacion),
          formatCurrency(p.totalInteres),
          formatCurrency(p.totalAmortizacion + p.totalInteres)
        ]);

        // Añadir fila de totales
        const totalAmort = proyeccionesAnuales.reduce((s, p) => s + p.totalAmortizacion, 0);
        const totalInt = proyeccionesAnuales.reduce((s, p) => s + p.totalInteres, 0);
        evolData.push(['TOTAL', formatCurrency(totalAmort), formatCurrency(totalInt), formatCurrency(totalAmort + totalInt)]);

        autoTable(doc, {
          startY: yPos,
          head: [['Año', 'Amortización', 'Intereses', 'Total Pago']],
          body: evolData,
          theme: 'grid',
          headStyles: {
            fillColor: hexToRgb(paleta.tabla.header),
            textColor: hexToRgb(paleta.tabla.headerText),
            fontStyle: 'bold',
            fontSize: 9
          },
          bodyStyles: { fontSize: 9 },
          alternateRowStyles: {
            fillColor: hexToRgb(paleta.tabla.row2)
          },
          columnStyles: {
            0: { cellWidth: 25, halign: 'center', fontStyle: 'bold' },
            1: { cellWidth: 40, halign: 'right' },
            2: { cellWidth: 40, halign: 'right' },
            3: { cellWidth: 40, halign: 'right', fontStyle: 'bold' }
          },
          margin: { left: margin, right: margin },
          tableWidth: 145,
          didParseCell: function(data) {
            // Resaltar fila de totales
            if (data.row.index === evolData.length - 1) {
              data.cell.styles.fillColor = hexToRgb(paleta.primario);
              data.cell.styles.textColor = [255, 255, 255];
              data.cell.styles.fontStyle = 'bold';
            }
          }
        });

        yPos = doc.lastAutoTable?.finalY + 10 || yPos + 20;
      }

      // ========== CUADRO DE AMORTIZACIÓN DETALLADO ==========
      if (seccionesActivas.proyecciones && operacionesLP && operacionesLP.length > 0) {
        // Calcular proyecciones detalladas para cada préstamo
        operacionesLP.forEach((op, idx) => {
          if (!op.capital_pendiente || op.capital_pendiente <= 0) return;
          if (!op.importe_solicitado || !op.cuota_mensual || !op.fecha_inicio || !op.fecha_fin) return;

          checkNewPage(70);
          
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(10);
          doc.setTextColor(...hexToRgb(paleta.primario));
          doc.text(`${idx + 1}. ${op.entidad_financiera} - ${op.tipologia_producto}`, margin, yPos);
          yPos += 6;

          // Info del préstamo
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8);
          doc.setTextColor(...hexToRgb(paleta.textoClaro));
          doc.text(`Capital Pendiente: ${formatCurrency(op.capital_pendiente)} | Cuota: ${formatCurrency(op.cuota_mensual)} | Vto: ${formatDate(op.fecha_fin)}`, margin, yPos);
          yPos += 8;

          // Generar cuadro simplificado (solo por año)
          const cuadroAnual = calcularCuadroAnual(op);
          
          if (cuadroAnual.length > 0) {
            autoTable(doc, {
              startY: yPos,
              head: [['Año', 'Cuotas', 'Amortización', 'Intereses', 'Capital Rest.']],
              body: cuadroAnual,
              theme: 'grid',
              headStyles: {
                fillColor: hexToRgb(paleta.tabla.header),
                textColor: hexToRgb(paleta.tabla.headerText),
                fontStyle: 'bold',
                fontSize: 7
              },
              bodyStyles: { fontSize: 7 },
              alternateRowStyles: {
                fillColor: hexToRgb(paleta.tabla.row2)
              },
              columnStyles: {
                0: { cellWidth: 20, halign: 'center' },
                1: { cellWidth: 20, halign: 'center' },
                2: { cellWidth: 30, halign: 'right' },
                3: { cellWidth: 30, halign: 'right' },
                4: { cellWidth: 30, halign: 'right' }
              },
              margin: { left: margin, right: margin },
              tableWidth: 130
            });

            yPos = doc.lastAutoTable?.finalY + 8 || yPos + 20;
          }
        });
      }

      // ========== PIE DE PÁGINA EN TODAS LAS PÁGINAS ==========
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        
        // Línea separadora
        doc.setDrawColor(...hexToRgb(paleta.secundario));
        doc.setLineWidth(0.5);
        doc.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);
        
        // Texto del pie
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...hexToRgb(paleta.textoClaro));
        doc.text(`Pool Bancario - ${empresa.nombre}`, margin, pageHeight - 7);
        doc.text(`Página ${i} de ${totalPages}`, pageWidth - margin, pageHeight - 7, { align: 'right' });
        
        // En la última página, añadir crédito de la aplicación
        if (i === totalPages) {
          doc.setFontSize(7);
          doc.setFont('helvetica', 'italic');
          doc.text('Realizado con la aplicación Pool Bancario del despacho PF Advisors', pageWidth / 2, pageHeight - 3, { align: 'center' });
        }
      }

      // Guardar PDF
      const nombreArchivo = `informe_pool_bancario_${empresa.nombre.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(nombreArchivo);

      toast.success('Informe PDF generado correctamente');
      onClose();

    } catch (error) {
      console.error('Error generando PDF:', error);
      toast.error('Error al generar el informe PDF');
    } finally {
      setGenerando(false);
    }
  };

  // Función para calcular cuadro de amortización anual simplificado
  const calcularCuadroAnual = (op) => {
    const hoy = new Date();
    const fechaFin = new Date(op.fecha_fin);
    const fechaInicio = new Date(op.fecha_inicio);
    
    const plazoTotalMeses = Math.round((fechaFin - fechaInicio) / (1000 * 60 * 60 * 24 * 30.44));
    const mesesPagados = Math.floor((hoy - fechaInicio) / (1000 * 60 * 60 * 24 * 30.44));
    const mesesRestantes = Math.max(1, plazoTotalMeses - mesesPagados);

    // Estimar tipo de interés si no está definido
    let tipoInteres = op.tipo_interes || 0;
    if (!tipoInteres && op.importe_solicitado && op.cuota_mensual) {
      const totalPagado = op.cuota_mensual * plazoTotalMeses;
      if (totalPagado > op.importe_solicitado) {
        let i = ((totalPagado - op.importe_solicitado) / op.importe_solicitado) / (plazoTotalMeses / 12) / 12;
        for (let iter = 0; iter < 50; iter++) {
          const factor = Math.pow(1 + i, plazoTotalMeses);
          const cuotaCalc = op.importe_solicitado * (i * factor) / (factor - 1);
          const error = cuotaCalc - op.cuota_mensual;
          if (Math.abs(error) < 0.01) break;
          const h = 0.00001;
          const factor2 = Math.pow(1 + i + h, plazoTotalMeses);
          const cuotaCalc2 = op.importe_solicitado * ((i + h) * factor2) / (factor2 - 1);
          const derivada = (cuotaCalc2 - cuotaCalc) / h;
          if (Math.abs(derivada) < 0.0001) break;
          i = Math.max(0.0001, Math.min(0.05, i - error / derivada));
        }
        tipoInteres = i * 12 * 100;
      }
    }

    const tipoInteresMensual = (tipoInteres / 100) / 12;
    let capitalRestante = op.capital_pendiente;
    
    // Calcular cuota fija
    let cuotaFija;
    if (tipoInteresMensual > 0) {
      const factor = Math.pow(1 + tipoInteresMensual, mesesRestantes);
      cuotaFija = op.capital_pendiente * (tipoInteresMensual * factor) / (factor - 1);
    } else {
      cuotaFija = op.capital_pendiente / mesesRestantes;
    }

    // Agrupar por año
    const porAño = {};
    let fechaCuota = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 1);

    for (let i = 0; i < mesesRestantes; i++) {
      const interesMes = capitalRestante * tipoInteresMensual;
      let amortMes = cuotaFija - interesMes;
      if (i === mesesRestantes - 1) amortMes = capitalRestante;

      const año = fechaCuota.getFullYear();
      if (!porAño[año]) {
        porAño[año] = { cuotas: 0, amort: 0, interes: 0 };
      }
      porAño[año].cuotas++;
      porAño[año].amort += amortMes;
      porAño[año].interes += interesMes;

      capitalRestante -= amortMes;
      fechaCuota.setMonth(fechaCuota.getMonth() + 1);
    }

    // Calcular capital restante al final de cada año
    let capRest = op.capital_pendiente;
    return Object.entries(porAño).sort(([a], [b]) => a - b).map(([año, data]) => {
      capRest -= data.amort;
      return [
        año,
        data.cuotas.toString(),
        formatCurrency(data.amort),
        formatCurrency(data.interes),
        formatCurrency(Math.max(0, capRest))
      ];
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pdf-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-['Outfit']">
            <FileText className="w-5 h-5 text-amber-500" />
            Generar Informe PDF
          </DialogTitle>
          <DialogDescription>
            Personaliza el contenido y estilo del informe antes de generarlo.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Selector de Paleta */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2 text-sm font-medium">
              <Palette className="w-4 h-4" />
              Paleta de Colores
            </Label>
            <Select value={paletaSeleccionada} onValueChange={setPaletaSeleccionada}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(PALETAS).map(([key, pal]) => (
                  <SelectItem key={key} value={key}>
                    <div className="flex items-center gap-3">
                      <div className="flex gap-1">
                        <div className="w-4 h-4 rounded" style={{ backgroundColor: pal.primario }} />
                        <div className="w-4 h-4 rounded" style={{ backgroundColor: pal.secundario }} />
                        <div className="w-4 h-4 rounded" style={{ backgroundColor: pal.acento }} />
                      </div>
                      <span>{pal.nombre}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Preview de la paleta */}
            <div className="flex items-center gap-2 p-3 rounded-lg bg-slate-50 border">
              <span className="text-xs text-slate-500">Vista previa:</span>
              <div className="flex gap-1">
                <div className="w-8 h-8 rounded shadow-sm" style={{ backgroundColor: paleta.primario }} title="Primario" />
                <div className="w-8 h-8 rounded shadow-sm" style={{ backgroundColor: paleta.secundario }} title="Secundario" />
                <div className="w-8 h-8 rounded shadow-sm" style={{ backgroundColor: paleta.acento }} title="Acento" />
                <div className="w-8 h-8 rounded shadow-sm border" style={{ backgroundColor: paleta.fondo }} title="Fondo" />
              </div>
            </div>
          </div>

          {/* Selector de Secciones */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Secciones a Incluir</Label>
            <div className="grid gap-3">
              {SECCIONES.map((seccion) => (
                <div 
                  key={seccion.id}
                  className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                    seccionesActivas[seccion.id] 
                      ? 'bg-amber-50 border-amber-200' 
                      : 'bg-slate-50 border-slate-200'
                  }`}
                >
                  <div className="flex-1">
                    <p className="font-medium text-sm">{seccion.nombre}</p>
                    <p className="text-xs text-slate-500">{seccion.descripcion}</p>
                  </div>
                  <Switch
                    checked={seccionesActivas[seccion.id]}
                    onCheckedChange={() => toggleSeccion(seccion.id)}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Resumen de datos */}
          <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
            <p className="text-sm font-medium text-blue-800 mb-2">Datos a incluir:</p>
            <ul className="text-xs text-blue-700 space-y-1">
              <li>• Empresa: {empresa?.nombre || 'No seleccionada'}</li>
              <li>• Operaciones L/P: {operacionesLP?.length || 0}</li>
              <li>• Operaciones C/P: {operacionesCP?.length || 0}</li>
              <li>• Años de proyección: {proyeccionesAnuales?.length || 0}</li>
            </ul>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={generando}>
            <X className="w-4 h-4 mr-2" />
            Cancelar
          </Button>
          <Button 
            onClick={generarPDF} 
            disabled={generando || !empresa}
            className="bg-amber-500 hover:bg-amber-600 text-slate-900"
          >
            {generando ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generando...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Generar PDF
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
